import {
  PUBLICATION_FORMATS,
  SERIES_STATUSES,
  type SeriesInput,
  type SeriesListItem,
} from '@panelvault/shared';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CoverUpload } from '../../components/CoverUpload';
import { Button } from '../../components/ui/button';
import { Select, Textarea } from '../../components/ui/controls';
import { Dialog } from '../../components/ui/dialog';
import { Field } from '../../components/ui/field';
import { Input } from '../../components/ui/input';
import { ApiError } from '../../lib/api';
import { useCreateSeries, usePublishers, useUpdateSeries } from './queries';

interface SeriesFormDialogProps {
  open: boolean;
  onClose: () => void;
  /** Omitted for a new series. */
  series?: SeriesListItem;
  onSaved?: (series: SeriesListItem) => void;
}

type FormState = {
  title: string;
  sortTitle: string;
  volume: string;
  publisherId: string;
  startYear: string;
  endYear: string;
  language: string;
  format: SeriesInput['format'];
  status: SeriesInput['status'];
  expectedIssueCount: string;
  description: string;
  notes: string;
  coverImageId: string | null;
};

const emptyForm: FormState = {
  title: '',
  sortTitle: '',
  volume: '',
  publisherId: '',
  startYear: '',
  endYear: '',
  language: 'de',
  format: 'issue',
  status: 'unknown',
  expectedIssueCount: '',
  description: '',
  notes: '',
  coverImageId: null,
};

function toForm(series: SeriesListItem): FormState {
  return {
    title: series.title,
    sortTitle: series.sortTitle ?? '',
    volume: series.volume ?? '',
    publisherId: series.publisherId ?? '',
    startYear: series.startYear?.toString() ?? '',
    endYear: series.endYear?.toString() ?? '',
    language: series.language ?? '',
    format: series.format,
    status: series.status,
    expectedIssueCount: series.expectedIssueCount?.toString() ?? '',
    description: series.description ?? '',
    notes: series.notes ?? '',
    coverImageId: series.coverImageId,
  };
}

function toPayload(form: FormState): SeriesInput {
  const number = (value: string) => (value.trim() === '' ? null : Number(value));
  const text = (value: string) => (value.trim() === '' ? null : value.trim());

  return {
    title: form.title.trim(),
    sortTitle: text(form.sortTitle),
    volume: text(form.volume),
    publisherId: form.publisherId || null,
    startYear: number(form.startYear),
    endYear: number(form.endYear),
    language: text(form.language),
    format: form.format,
    status: form.status,
    expectedIssueCount: number(form.expectedIssueCount),
    description: text(form.description),
    notes: text(form.notes),
    coverImageId: form.coverImageId,
  };
}

export function SeriesFormDialog({ open, onClose, series, onSaved }: SeriesFormDialogProps) {
  const { t } = useTranslation();
  const ids = useId();
  const publishers = usePublishers();
  const createSeries = useCreateSeries();
  const updateSeries = useUpdateSeries();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(series ? toForm(series) : emptyForm);
    setError(null);
  }, [open, series]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const pending = createSeries.isPending || updateSeries.isPending;

  async function submit() {
    if (form.title.trim() === '') {
      setError(t('form.titleRequired'));
      return;
    }

    try {
      const payload = toPayload(form);
      const saved = series
        ? await updateSeries.mutateAsync({ id: series.id, patch: payload })
        : await createSeries.mutateAsync(payload);

      onSaved?.(saved);
      onClose();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t('form.saveFailed'));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={series ? t('series.edit') : t('series.create')}
      description={series ? t('form.overrideHint') : undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            {t('form.cancel')}
          </Button>
          <Button onClick={() => void submit()} disabled={pending}>
            {pending ? t('form.saving') : t('form.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('series.title')} htmlFor={`${ids}-title`}>
          <Input
            id={`${ids}-title`}
            value={form.title}
            onChange={(event) => set('title', event.target.value)}
            autoFocus
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('series.volume')} htmlFor={`${ids}-volume`} hint={t('series.volumeHint')}>
            <Input
              id={`${ids}-volume`}
              value={form.volume}
              onChange={(event) => set('volume', event.target.value)}
            />
          </Field>

          <Field label={t('series.publisher')} htmlFor={`${ids}-publisher`}>
            <Select
              id={`${ids}-publisher`}
              value={form.publisherId}
              onChange={(event) => set('publisherId', event.target.value)}
            >
              <option value="">{t('form.none')}</option>
              {publishers.data?.map((publisher) => (
                <option key={publisher.id} value={publisher.id}>
                  {publisher.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('series.startYear')} htmlFor={`${ids}-start`}>
            <Input
              id={`${ids}-start`}
              inputMode="numeric"
              value={form.startYear}
              onChange={(event) => set('startYear', event.target.value)}
            />
          </Field>

          <Field label={t('series.endYear')} htmlFor={`${ids}-end`}>
            <Input
              id={`${ids}-end`}
              inputMode="numeric"
              value={form.endYear}
              onChange={(event) => set('endYear', event.target.value)}
            />
          </Field>

          <Field label={t('series.format')} htmlFor={`${ids}-format`}>
            <Select
              id={`${ids}-format`}
              value={form.format}
              onChange={(event) => set('format', event.target.value as FormState['format'])}
            >
              {PUBLICATION_FORMATS.map((format) => (
                <option key={format} value={format}>
                  {t(`format.${format}`)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('series.status')} htmlFor={`${ids}-status`}>
            <Select
              id={`${ids}-status`}
              value={form.status}
              onChange={(event) => set('status', event.target.value as FormState['status'])}
            >
              {SERIES_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`status.${status}`)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('series.language')} htmlFor={`${ids}-language`}>
            <Input
              id={`${ids}-language`}
              value={form.language}
              maxLength={5}
              onChange={(event) => set('language', event.target.value)}
            />
          </Field>

          <Field
            label={t('series.expectedCount')}
            htmlFor={`${ids}-expected`}
            hint={t('series.expectedCountHint')}
          >
            <Input
              id={`${ids}-expected`}
              inputMode="numeric"
              value={form.expectedIssueCount}
              onChange={(event) => set('expectedIssueCount', event.target.value)}
            />
          </Field>
        </div>

        <CoverUpload
          label={t('form.cover')}
          imageId={form.coverImageId}
          onChange={(id) => set('coverImageId', id)}
        />

        <Field label={t('form.notes')} htmlFor={`${ids}-notes`}>
          <Textarea
            id={`${ids}-notes`}
            value={form.notes}
            onChange={(event) => set('notes', event.target.value)}
          />
        </Field>

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
